
import { Button } from "@/components/ui/button";
import {

  useGetAllMerchant,
  useUpdateTerminal,
} from "./mutations";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  terminalId: z.string().min(1, "Terminal ID is required"),
  serialNumber: z
    .string()
    .min(1, "Serial Number is required")
    .nonempty()
    .readonly(),
  merchantId: z.string().min(1, "Merchant ID is required"),
  terminalName: z.string().min(1, "Terminal Name is required"),
  terminalAddress: z.string().min(1, "Terminal Address is required"),
  customerType: z.string().min(1, "Customer Type is required"),
  routeCode: z.string().min(1, "Route Code is required"),
  adminPassword: z.string().min(1, "Admin Password is required"),
  reprintPassword: z.string().min(1, "Reprint Password is required"),
  terminalModel: z.string().min(1, "Terminal Model is required"),
  maximumAmount: z.number().min(1, "Maximum Amount is required"),
});
export type TerminalDTO = z.infer<typeof formSchema>;

const EditTerminalModal = ({
  onCancel,
  isOpenEdit,
  isLoading,
  terminal,
}: {
  onCancel: () => void;
  isOpenEdit: boolean;
  isLoading?: boolean;
  terminal: any;
}) => {
  const form = useForm<TerminalDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: terminal,
  });
  const { data, } = useGetAllMerchant({
    size: 1000000,
    page: 1,
  });

  const { mutate: updateTerminal, isPending } = useUpdateTerminal({
    id: terminal.terminalId,
  });

  const onSubmit = (data: TerminalDTO) => {
    updateTerminal(data);
  };
  return (
    <Dialog open={isOpenEdit} onOpenChange={onCancel}>
      <DialogContent className="h-[75%] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Terminal</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="terminalId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terminal ID</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serialNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial Number</FormLabel>
                  <FormControl>
                    <Input readOnly {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isLoading && (
              <FormField
                control={form.control}
                name="merchantId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Merchant</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={`${field.value}`}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Merchant" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {data?.merchants.map((v, key) => (
                          <SelectItem key={key} value={v.merchantId}>
                            {v.merchantName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="terminalName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terminal Name</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="terminalAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terminal Address</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="customerType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">Customer Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={`${field.value}`}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Customer Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {["Agent", "Merchant"].map((v, key) => (
                        <SelectItem key={key} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="routeCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Route Code</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={`${field.value}`}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Route Code" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={"001"}>{"001"}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="adminPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Admin Password</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reprintPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reprint Password</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="terminalModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terminal Model</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maximumAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Amount</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      className="h-12"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button disabled={isPending} isLoading={isPending} type="submit">
                Submit
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTerminalModal;
